import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const LANGUAGES = ["en", "ja"];
const STORAGE_KEY = "dp-lang";

// A visitor's explicit choice wins; otherwise follow the browser, so Japanese
// visitors land in Japanese. Storage can throw (private mode, blocked site
// data), so every access is guarded and the browser language is the fallback.
function initialLanguage() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (LANGUAGES.includes(saved)) return saved;
  } catch (_) {
    // fall through to the browser language
  }
  const nav = typeof navigator === "undefined" ? null : navigator;
  const preferred = (nav && (nav.languages?.[0] || nav.language)) || "";
  return preferred.toLowerCase().startsWith("ja") ? "ja" : "en";
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLanguage);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Only an explicit toggle is remembered; a detected language is not stored,
  // so a visitor who never switches keeps following their browser.
  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      // the choice still applies for this visit
    }
  }, []);

  // Resolves a `{ en, ja }` copy entry to the current language; plain values
  // (names, URLs) pass through unchanged.
  const t = useCallback(
    (value) =>
      value && typeof value === "object" && "en" in value ? value[lang] : value,
    [lang]
  );

  const ctx = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return (
    <LanguageContext.Provider value={ctx}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}
